/**
 * Loading placeholder for the product detail view.
 *
 * Rendered by ProductPage while `productDetail` is null (cold navigation / ISR
 * `fallback: true` render) INSTEAD of ProductDetail. ProductDetail has no
 * loading gate, so a null product renders an empty shell — blank `<h1>`, "$0",
 * and the `/images/logo.png` fallback in the gallery — which reads as broken.
 *
 * WHY IT REUSES THE REAL CLASS NAMES. The first version laid itself out with its
 * own Tailwind flexbox and hand-picked widths, which is a guess at the finished
 * page rather than a copy of it: the real page is a CSS grid (`.product-top`,
 * styles/pages/product.scss) with fixed rows and three columns, and the guess
 * matched none of it, so everything moved when the content swapped in. Every box
 * below carries the class the loaded page uses for that box, so the SAME
 * stylesheet lays both out and the geometry cannot drift — including at the
 * $medium and $small breakpoints, where the grid reflows to one column, the
 * thumbnail strip disappears and the buy box becomes a fixed bottom bar.
 *
 * Verified box-for-box against the loaded page at 1440px. Both render:
 *
 *   .category-breadcrumbs      x=0    1440 x 46.5
 *   .product-top               x=0    1440 x 810   (grid, 5 fixed rows)
 *   .product-images-piece      x=24    640 x 810   (col 1, rows 1 / -1)
 *   .product-images            x=24    640 x 640   square, 1px border, r=10
 *   .product-thumb-photo       x=24   ~151 x 151   four across, filling the column
 *   h1.product-name            x=696   388 x 100   (row 1)
 *   .user-info                 x=696   388 x 70    (row 2)
 *   .user-viz                  x=696   152 x 30
 *   .price-line                x=696   388 x 70    (row 3)
 *   .product-description-cont  x=696   388 x 280   (row 4)
 *   .order-info                x=1116  300 x 520   (col 3, rows 1-4)
 *   .order-info-inner          x=1117  298 x 518
 *   .order-detail-heading      x=1117  298 x 48
 *   .product-detail-ad         x=740   300 x 250   (row 5)
 *
 * Every row track is a fixed pixel height, which is what lets this component
 * work at all: the placeholder and the loaded page occupy the same boxes no
 * matter what either contains. 100 + 70 + 70 + 280 = 520 = `.order-info`, and
 * + 290 for the ad row = 810 = this gallery column.
 *
 * TWO SIZING RULES:
 *
 *  1. Tailwind's scale is absolute pixels app-wide (the `:root` block in
 *     styles/tailwind.css), so `h-4` on a bar means 16px and the numbers below
 *     say what they mean. A literal `[Npx]` appears only where a box is matched
 *     to a measurement off the 4px step (the 50px button, the 107px protection
 *     card).
 *
 *     `skeleton-scope` on the bars is now only a marker. It used to carry that
 *     re-basing for the bars alone, back when the scale was scoped and a
 *     Tailwind utility paid out 62.5% of its nominal size everywhere else — the
 *     wrappers deliberately went without it so their copied-verbatim classes
 *     stayed in step with the loaded component. With one global scale both
 *     sides match by construction and there is nothing left to opt into.
 *
 *  2. A bar standing in for a LINE OF TEXT holds a non-breaking space instead of
 *     a height, so its box is the real line box of its container's own type.
 *     That is exact where a pixel guess is not, and it follows the font.
 *
 * The fill is the app-wide `.skeleton` shimmer, which reads its colours from the
 * theme (styles/theme.scss). It used to be a hard-coded near-black gradient —
 * i.e. black blocks on the light theme's #eee page.
 */

/** A plain block bar. */
const Bar = ({ className }: { className: string }) => (
  <div className={`skeleton skeleton-scope rounded-md ${className}`} />
);

/** A bar occupying exactly one line box of its container's type — rule 2. */
const TextBar = ({ className }: { className: string }) => (
  <div className={`skeleton skeleton-scope rounded-md ${className}`}>&nbsp;</div>
);

/** A fully-rounded fill: avatars and pill buttons. */
const Round = ({ className }: { className: string }) => (
  <div className={`skeleton skeleton-scope rounded-full ${className}`} />
);

/** A label/figure row in the buy box, at the 50px the loaded rows use. */
const PriceRow = ({ label, value }: { label: string; value: string }) => (
  <div className="h-[50px] p-5 flex w-full justify-between items-center">
    <Bar className={`h-3 ${label}`} />
    <Bar className={`h-3 ${value}`} />
  </div>
);

const ProductSkeleton = () => (
  <div
    className="product product-skeleton"
    data-testid="product-skeleton"
    aria-busy="true"
    aria-label="Loading listing"
  >
    <div className="category-breadcrumbs">
      <TextBar className="w-56 max-w-full" />
    </div>

    <section className="product-top">
      {/* Gallery — grid column 1, spanning every row. */}
      <div className="product-images-piece">
        <div className="product-images same-color-border">
          <div className="product-images-cont skeleton" />
        </div>
        <div className="product-image-thumbs">
          <div className="product-image-thumbs-cont">
            <div className="product-thumbs">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={`thumb-${i}`}
                  className="product-thumb-photo same-color-border skeleton"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Title — two line boxes of 3.5rem, which is what fills the 100px row. */}
      <h1 className="product-name">
        <TextBar className="w-full" />
        <TextBar className="w-3/5 mt-1.5" />
      </h1>

      {/* Seller row — avatar + name left, share/report right. */}
      <div className="user-info">
        <div className="user-info-cont">
          <div className="user-viz">
            <div className="profile-pic">
              <Round className="w-[30px] h-[30px]" />
            </div>
            <div className="user-name">
              {/* shrink-0 or the bar collapses and `.user-viz` comes out 110px
                  wide instead of the loaded page's 152px. */}
              <TextBar className="w-28 shrink-0" />
            </div>
          </div>
        </div>
        <div className="action-icons">
          <div className="action-button">
            <Bar className="w-4 h-[18px]" />
          </div>
          <div className="action-button">
            <Bar className="w-4 h-[18px]" />
          </div>
        </div>
      </div>

      {/* Price row — price left, location right, as the loaded row splits. */}
      <div className="price-line">
        <div className="product-price-group">
          <div className="product-price">
            <Bar className="h-8 w-32" />
          </div>
        </div>
        <Bar className="h-3 w-24" />
      </div>

      {/* Description — the loaded block is 388 x 216 inside this 280px cell. */}
      <div className="product-description-cont">
        <div className="product-description">
          <Bar className="h-3.5 w-28 mb-3.5" />
          <div className="flex flex-col gap-2">
            <Bar className="h-3 w-full" />
            <Bar className="h-3 w-11/12" />
            <Bar className="h-3 w-full" />
            <Bar className="h-3 w-4/5" />
            <Bar className="h-3 w-2/3" />
          </div>
        </div>
      </div>

      {/*
        Buy box. The bottom group is `mt-auto`, not spaced down from the pricing
        block: bottom-aligning 244px of content inside the 518px inner box puts
        the button at exactly the loaded page's y (522 at 1440px) without
        depending on how many price rows a given listing shows — a vehicle, a
        non-shippable and an adoption listing each render a different count.
      */}
      <div className="order-info same-color-2 same-color-border">
        <div className="order-info-inner">
          <div className="order-info-pricing">
            <div className="order-detail-heading same-color-border">
              <Bar className="h-3.5 w-32" />
            </div>
            <PriceRow label="w-20" value="w-14" />
            <PriceRow label="w-24" value="w-16" />
            <PriceRow label="w-16" value="w-20" />
            <PriceRow label="w-12" value="w-24" />
          </div>
          <div className="mt-auto">
            <Round className="h-[50px] w-[200px] mx-auto" />
            <Bar className="h-5 w-24 mx-auto mt-[34px]" />
            <Bar className="h-[107px] w-full mt-[33px]" />
          </div>
        </div>
      </div>

      {/*
        Reserves the ad frame's box so nothing shifts when the real AdFramework
        mounts with the product. Deliberately NOT shimmered: an ad slot is not
        content the reader is waiting on, and animating one would promise
        something that may never fill.
      */}
      <div className="product-detail-ad" aria-hidden="true">
        <div className="w-[300px] h-[250px] mx-auto" />
      </div>
    </section>
  </div>
);

export default ProductSkeleton;
